import { NavLink, useLocation } from 'react-router';
import { Eyebrow, token } from '@bp-files/ui';
import { APP_COPY } from '@/copy/app.copy';
import { FilesNav } from '@/components/FilesNav';
import { domainOf, SETTINGS_HOME, settingsGroups } from '@/surfaces/surfaces';

export interface SurfaceNavProps {
  /** Drives which entries are listed. Not an authorization control — see SEC-5. */
  isAdmin: boolean;
}

/**
 * Sidebar navigation, scoped to whichever domain the rail currently has
 * selected (`domainOf`) — the Files domain lists every folder destination the
 * caller can open (`FilesNav`); the Settings domain lists the settings groups.
 *
 * **Settings is two groups now, and it is no longer admin-only** (ADR-0064).
 * Everybody gets "Your account" — General, Secure sign-in, Your signature — and
 * an admin gets "Agency" below it, carrying the four surfaces that used to be
 * the whole of this domain. A member arriving here finds three real pages rather
 * than the empty column they would have found before, which is why the rail's
 * gear no longer has to be hidden from them.
 *
 * Real spaces, not buttons: a surface is a URL (ADR-0007), so the browser's own
 * affordances — middle-click, copy address, back — come for free, and the
 * active state follows the route rather than a prop somebody has to remember to
 * pass.
 *
 * Active-state treatment: blue label and a 3px blue rail on the leading edge.
 * No fill behind the active item — just the label colour and the rail.
 */
export function SurfaceNav({ isAdmin }: SurfaceNavProps) {
  const domain = domainOf(useLocation().pathname);

  // No `isAdmin`: the Files rail is one list of destinations for both roles
  // (FIL-1e). The Settings groups below still branch on it.
  if (domain === 'files') return <FilesNav />;

  return (
    <div className="flex flex-col gap-1">
      {/*
        The Settings wordmark — `FilesNav`'s own treatment for its "Files"
        title, one level down from the rail icon. Unconditionally styled
        (never the active/inactive colour the entries below it use), the same
        call `FilesNav` makes for its wordmark: it names the domain you
        switched into rather than competing with the entries for "current"
        styling. Spaces to `SETTINGS_HOME`, the same landing route the rail's
        Settings icon uses — the two must not be able to disagree, which is why
        neither of them spells the path out.
      */}
      {/*
        `mb-3` beyond this container's own `gap-1`: the wordmark and the first
        group heading read as cramped at 4px, on `FilesNav`'s identical fix and
        for the identical reason — the extra space is scoped to the wordmark
        alone, not to the list's own tight `gap-1` between entries.
      */}
      <NavLink
        to={SETTINGS_HOME}
        className="truncate rounded-r-md px-3 pt-1.5 pb-2 text-h2 font-semibold leading-none tracking-tight mb-3"
        // `bp-text`, not `bp-on-accent` — see `BrandMark`'s header. The two
        // are indistinguishable on the command palette and are ink versus a
        // white-on-white wordmark on the daylight one (ADR-0064).
        style={{ color: token('bp-text') }}
      >
        {APP_COPY.nav.settings}
      </NavLink>

      {settingsGroups(isAdmin).map((group) => (
        <div
          key={group.id}
          // `mt-4` on every group but the first: the wordmark already carries
          // its own `mb-3` above, so a top margin here would compound into a
          // gap twice the size of the one between groups.
          className="flex flex-col gap-1 first:mt-0 mt-4"
        >
          {/*
            An `<Eyebrow>` in its default `span`, **not `as="h2"`**. The
            treatment is right — this is exactly the uppercase micro-label the
            design system uses to open a group — but the document outline has
            one owner, `App.test.tsx`'s `App — the heading outline`, and it is
            about the page. Two headings in the sidebar would be in that outline
            on every route, above whatever the page itself called its `<h1>`.
          */}
          <Eyebrow className="px-3 pb-1">{group.label}</Eyebrow>

          {/*
            `aria-label` on the list rather than a landmark around the group: a
            screen reader gets "Your account, list, 3 items", which is the
            division that matters here, without adding two `region`s inside a
            `navigation` that is already one.
          */}
          <ul aria-label={group.label} className="flex flex-col gap-1">
            {group.entries.map((entry) => (
              <li key={entry.id}>
                <NavLink
                  to={entry.path}
                  // Prefix matching, deliberately left at the default: a shared
                  // space lives under /files, and Files should stay the current
                  // surface while the user is inside one. It costs nothing here
                  // — no settings path is a prefix of another.
                  className="flex w-full items-center rounded-r-md px-3 py-2 text-sm font-medium transition-colors"
                  style={({ isActive }) => ({
                    color: isActive ? token('bp-blue-text') : token('bp-text-muted'),
                    borderLeftWidth: token('rail-indicator'),
                    borderLeftStyle: 'solid',
                    borderLeftColor: isActive ? token('bp-blue-rail') : 'transparent',
                  })}
                >
                  {entry.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
